<template>
  <div class="booking-intake-tab mx-2 mb-4">
    <div class="alert alert-light border small mb-3 py-2">
      <strong>Missed meetings</strong> — bookings captured from API logs / intake queue
      that may not exist fully in Oliver (<code>link_event</code> / <code>calendar_event</code>).
      Use <strong>Recover</strong> to diagnose each case and create/attach in Oliver
      without rebooking Outlook/Teams. Default filter: <code>actionable</code>
      (<code>needs_recovery</code> + <code>failed</code>).
    </div>

    <div class="card border-0 shadow-sm mb-3">
      <div class="card-body py-2 d-flex flex-wrap align-items-center">
        <div class="d-flex align-items-center mr-3 mb-1">
          <span class="small text-muted mr-2">Status</span>
          <select
            v-model="filters.status"
            class="form-control form-control-sm"
            style="width: 160px;"
            @change="loadItems"
          >
            <option value="actionable">actionable (needs_recovery + failed)</option>
            <option value="needs_recovery">needs_recovery</option>
            <option value="failed">failed</option>
            <option value="external_created">external_created</option>
            <option value="received">received</option>
            <option value="processed">processed</option>
            <option value="ignored">ignored</option>
            <option value="all">all</option>
          </select>
        </div>
        <div class="d-flex align-items-center mr-3 mb-1">
          <span class="small text-muted mr-2">Source</span>
          <select
            v-model="filters.source"
            class="form-control form-control-sm"
            style="width: 140px;"
            @change="loadItems"
          >
            <option value="all">all</option>
            <option value="amazon_log">amazon_log</option>
            <option value="api">api</option>
            <option value="manual">manual</option>
          </select>
        </div>
        <div class="d-flex align-items-center mr-3 mb-1">
          <span class="small text-muted mr-2">Client ID</span>
          <input
            v-model="filters.client_id"
            type="text"
            class="form-control form-control-sm"
            style="width: 100px;"
            placeholder="e.g. 6263"
            @keyup.enter="loadItems"
          />
        </div>
        <div class="d-flex align-items-center mr-3 mb-1">
          <span class="small text-muted mr-2">Search</span>
          <input
            v-model="filters.q"
            type="text"
            class="form-control form-control-sm"
            style="width: 220px;"
            placeholder="email / company / provider"
            @keyup.enter="loadItems"
          />
        </div>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary mb-1"
          :disabled="loading"
          @click="loadItems"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm mr-1" />
          Refresh
        </button>
        <span v-if="total !== null" class="small text-muted ml-auto mb-1">
          {{ total }} rows shown
          <template v-if="statusCountsText"> · {{ statusCountsText }}</template>
        </span>
      </div>
    </div>

    <LoadingSpinner v-if="loading" title="Loading missed meetings…" />

    <p v-else-if="!items.length" class="text-muted small mb-0">
      No booking intake rows for these filters.
    </p>

    <div v-else class="table-responsive">
      <table class="table table-sm table-hover bg-white border">
        <thead class="thead-light">
          <tr>
            <th>ID</th>
            <th>Status</th>
            <th>Source</th>
            <th>Created</th>
            <th>Invitee</th>
            <th>Company</th>
            <th>Meeting</th>
            <th>Calendar mailbox</th>
            <th>Client / Sale</th>
            <th>In Oliver</th>
            <th>Error</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in items"
            :key="row.id"
            :class="rowRowClass(row)"
          >
            <td class="font-weight-bold">#{{ row.id }}</td>
            <td>
              <span class="badge" :class="statusBadgeClass(row.status)">
                {{ row.status }}
              </span>
              <div v-if="row.retry_count > 0" class="text-muted" style="font-size: 11px;">
                retries {{ row.retry_count }}
              </div>
            </td>
            <td>
              <span class="badge badge-light border">{{ row.source }}</span>
            </td>
            <td class="text-nowrap small text-muted">
              {{ formatDateTime(row.created) }}
            </td>
            <td>
              <div>{{ row.invitee_email }}</div>
            </td>
            <td>
              <div>{{ displayCompany(row) }}</div>
            </td>
            <td>
              <div>{{ formatDateTime(row.start_time) }}</div>
              <span
                class="badge badge-light border"
                :class="row.is_past == 1 ? 'text-warning' : 'text-muted'"
              >
                {{ row.is_past == 1 ? 'past/due' : 'upcoming' }}
              </span>
            </td>
            <td>
              <div>{{ row.provider_email || '—' }}</div>
              <div class="text-muted" style="font-size: 11px;">
                {{ row.provider || '—' }}
                <span v-if="row.provider_event_id"> · has provider id</span>
              </div>
            </td>
            <td>
              <template v-if="row.client_id">
                <a :href="'/clients/' + row.client_id" target="_blank">
                  {{ row.client_name || ('#' + row.client_id) }}
                </a>
              </template>
              <span v-else class="text-muted">—</span>
              <div class="text-muted" style="font-size: 11px;">
                {{ (row.sale_user_name || '').trim() || row.sale_user_email || ('user #' + (row.sale_user_id || '—')) }}
              </div>
              <div v-if="row.campaign_id" class="text-muted" style="font-size: 11px;">
                campaign #{{ row.campaign_id }}
              </div>
            </td>
            <td>
              <span
                class="badge"
                :class="row.in_oliver == 1 ? 'badge-success' : 'badge-secondary'"
              >
                {{ row.in_oliver == 1 ? 'yes' : 'no' }}
              </span>
              <div v-if="row.link_id" class="small mt-1">
                <a :href="'/opportunities/' + row.link_id" target="_blank">
                  link #{{ row.link_id }}
                </a>
              </div>
              <div v-if="linkMatchKind(row)" class="mt-1">
                <span
                  class="badge"
                  :class="linkMatchBadgeClass(linkMatchKind(row))"
                  style="font-size: 10px;"
                >
                  {{ linkMatchLabel(linkMatchKind(row)) }}
                </span>
              </div>
              <div v-if="row.calendar_event_id" class="text-muted" style="font-size: 11px;">
                ce #{{ row.calendar_event_id }}
              </div>
            </td>
            <td class="small" :class="linkMatchErrorClass(row)" style="max-width: 240px;">
              {{ displayErrorMessage(row) }}
            </td>
            <td class="text-nowrap">
              <button
                v-if="canRecover(row)"
                type="button"
                class="btn btn-sm btn-outline-primary"
                :disabled="diagnosingId === row.id || recoveringId === row.id"
                @click="openDiagnose(row)"
              >
                <span
                  v-if="diagnosingId === row.id"
                  class="spinner-border spinner-border-sm mr-1"
                />
                Recover
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Modal
      name="booking-intake-recover-modal"
      :size="'50'"
      :scrollable="true"
      @closeModal="closeRecoverModal"
    >
      <div class="p-4" v-if="selectedRow">
        <template v-if="modalMode === 'confirm' && diagnosis">
          <h5 class="mb-3">
            Recover meeting — intake #{{ selectedRow.id }}
          </h5>

          <div class="small mb-3">
            <div>
              <strong>Case:</strong>
              <span class="badge ml-1" :class="caseBadgeClass(diagnosis.case)">
                {{ diagnosis.case }}
              </span>
              <span class="badge badge-light border ml-1">{{ diagnosis.confidence }}</span>
            </div>
            <div class="mt-2"><strong>Invitee:</strong> {{ diagnosis.invitee_email || '—' }}</div>
            <div><strong>Company:</strong> {{ diagnosis.company_name || '—' }}</div>
            <div><strong>Meeting:</strong> {{ formatDateTime(diagnosis.start_time) }}</div>
            <div>
              <strong>Campaign:</strong>
              <template v-if="diagnosis.campaign_id">
                #{{ diagnosis.campaign_id }} — {{ diagnosis.campaign_name || '—' }}
              </template>
              <span v-else>—</span>
            </div>
            <div v-if="diagnosis.link_id || (diagnosis.existing && diagnosis.existing.link_id)">
              <strong>Link:</strong>
              <a
                :href="'/opportunities/' + (diagnosis.existing && diagnosis.existing.link_id ? diagnosis.existing.link_id : diagnosis.link_id)"
                target="_blank"
              >
                #{{ diagnosis.existing && diagnosis.existing.link_id ? diagnosis.existing.link_id : diagnosis.link_id }}
              </a>
              <span
                v-if="diagnosis.existing && diagnosis.existing.match_kind === 'same_day'"
                class="badge badge-warning ml-1"
              >same day</span>
            </div>
            <div v-if="diagnosis.link_id_from_log" class="text-muted" style="font-size: 11px;">
              link_id from log: #{{ diagnosis.link_id_from_log }}
            </div>
            <div><strong>Action:</strong> {{ actionLabel(selectedAction) }}</div>
          </div>

          <div
            v-if="diagnosis.duplicate_warning || (diagnosis.existing_same_day && diagnosis.existing_same_day.length)"
            class="alert alert-warning border small mb-3 py-2"
          >
            <strong class="d-block mb-2">Already in Oliver (do not create)</strong>
            <p v-if="diagnosis.duplicate_warning" class="mb-2">{{ diagnosis.duplicate_warning }}</p>
            <div
              v-for="ce in diagnosis.existing_same_day"
              :key="'ce-' + ce.calendar_event_id"
              class="mb-1"
            >
              Existing meeting
              <strong>{{ formatDateTime(ce.start_time) }}</strong>
              <template v-if="ce.link_id">
                on
                <a :href="'/opportunities/' + ce.link_id" target="_blank">#{{ ce.link_id }}</a>
              </template>
              <span class="text-muted"> · ce #{{ ce.calendar_event_id }}</span>
            </div>
            <div class="text-muted mt-2" style="font-size: 11px;">
              Use <strong>Mark processed</strong> to close this intake without creating a duplicate.
            </div>
          </div>

          <div
            v-if="diagnosis.company_candidates && diagnosis.company_candidates.length"
            class="alert alert-info border small mb-3 py-2"
          >
            <strong class="d-block mb-2">Same company opportunities</strong>
            <div
              v-for="cand in diagnosis.company_candidates"
              :key="'cand-' + cand.link_id"
              class="d-flex align-items-start mb-2"
            >
              <input
                class="mt-1 mr-2"
                type="radio"
                :id="'cand-' + cand.link_id"
                :value="cand.link_id"
                v-model.number="selectedTargetLinkId"
                :disabled="selectedAction !== 'attach_to_link'"
              />
              <label class="mb-0" :for="'cand-' + cand.link_id">
                <a :href="'/opportunities/' + cand.link_id" target="_blank" class="font-weight-bold">
                  #{{ cand.link_id }}
                </a>
                — {{ cand.business_name || '—' }}
                <span class="badge badge-light border ml-1">{{ cand.match_reason }}</span>
                <div class="text-muted" style="font-size: 11px;">
                  contact {{ cand.contact_email || '—' }}
                  · campaign #{{ cand.campaign_id || '—' }}
                  · meetings {{ cand.meeting_count }}
                </div>
              </label>
            </div>
            <div class="text-muted" style="font-size: 11px;">
              Prefer attach when the company already exists (even if invitee email is a different contact).
            </div>
          </div>

          <div class="alert alert-light border small mb-3">
            <strong class="d-block mb-2">Planned actions</strong>
            <ol class="mb-0 pl-3">
              <li v-for="(step, index) in diagnosis.steps" :key="'step-' + index">
                {{ step }}
              </li>
            </ol>
          </div>

          <p class="small text-muted mb-3">{{ diagnosis.summary }}</p>

          <div
            v-if="diagnosis.allowed_actions && diagnosis.allowed_actions.length > 1"
            class="mb-3"
          >
            <span class="small text-muted d-block mb-1">Override action</span>
            <select
              v-model="selectedAction"
              class="form-control form-control-sm"
              style="max-width: 260px;"
              @change="onActionChange"
            >
              <option
                v-for="act in diagnosis.allowed_actions"
                :key="act"
                :value="act"
              >
                {{ actionLabel(act) }}
              </option>
            </select>
          </div>

          <div class="d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-outline-secondary mr-2"
              :disabled="recoveringId === selectedRow.id"
              @click="closeRecoverModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn"
              :class="selectedAction === 'ignore' ? 'btn-outline-danger' : 'btn-primary'"
              :disabled="recoveringId === selectedRow.id"
              @click="confirmRecover"
            >
              <span
                v-if="recoveringId === selectedRow.id"
                class="spinner-border spinner-border-sm mr-1"
              />
              Confirm {{ actionLabel(selectedAction) }}
            </button>
          </div>
        </template>

        <template v-else>
          <h5 class="mb-3">
            Cannot recover intake #{{ selectedRow.id }}
          </h5>

          <div class="alert alert-warning small mb-3">
            <strong>{{ blockedTitle }}</strong>
            <p class="mb-0 mt-2">{{ blockedMessage }}</p>
          </div>

          <div class="d-flex justify-content-end">
            <button type="button" class="btn btn-primary" @click="closeRecoverModal">
              OK
            </button>
          </div>
        </template>
      </div>
    </Modal>
  </div>
</template>

<script>
import { targetsService } from "@/_services";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Modal from "@/components/Common/Modal.vue";

export default {
  name: "BookingIntakeTab",
  components: {
    LoadingSpinner,
    Modal,
  },
  data() {
    return {
      loading: false,
      items: [],
      total: null,
      statusCounts: {},
      filters: {
        status: "actionable",
        source: "all",
        client_id: "",
        q: "",
      },
      selectedRow: null,
      modalMode: "confirm",
      diagnosis: null,
      selectedAction: null,
      selectedTargetLinkId: null,
      diagnosingId: null,
      recoveringId: null,
      blockedTitle: "Cannot recover",
      blockedMessage: "",
    };
  },
  computed: {
    statusCountsText() {
      if (!this.statusCounts || !Object.keys(this.statusCounts).length) {
        return "";
      }
      return Object.keys(this.statusCounts)
        .map((k) => `${k}:${this.statusCounts[k]}`)
        .join(" · ");
    },
  },
  created() {
    this.loadItems();
  },
  methods: {
    loadItems() {
      this.loading = true;
      const params = {
        status: this.filters.status,
        source: this.filters.source,
        client_id: this.filters.client_id || null,
        q: this.filters.q || null,
        limit: 300,
      };
      targetsService
        .getBookingIntake(params)
        .then((result) => {
          this.items = (result && result.items) || [];
          this.total = result && result.total != null ? result.total : this.items.length;
          this.statusCounts = (result && result.status_counts) || {};
        })
        .catch(() => {
          this.items = [];
          this.total = 0;
          this.statusCounts = {};
        })
        .finally(() => {
          this.loading = false;
        });
    },
    canRecover(row) {
      return ["needs_recovery", "failed", "external_created"].indexOf(row.status) !== -1;
    },
    displayCompany(row) {
      const name = row.company_name || "";
      if (!name || name.indexOf('{"') === 0 || name.indexOf('",') !== -1 || name.length > 120) {
        return "—";
      }
      return name;
    },
    openDiagnose(row) {
      this.diagnosingId = row.id;
      targetsService
        .diagnoseBookingIntake(row.id)
        .then((result) => {
          if (!result || result.error) {
            this.openBlockedModal(row, {
              title: "Diagnose failed",
              message: (result && result.error) || "Could not diagnose this booking.",
            });
            return;
          }
          if (result.recommended_action === "blocked" || !result.allowed_actions || !result.allowed_actions.length) {
            this.openBlockedModal(row, {
              title: "No recoverable action",
              message: result.summary || "Cannot recover this row safely.",
            });
            return;
          }
          this.selectedRow = row;
          this.modalMode = "confirm";
          this.diagnosis = result;
          this.selectedAction = result.recommended_action;
          this.selectedTargetLinkId =
            result.recommended_link_id ||
            (result.company_candidates && result.company_candidates[0]
              ? result.company_candidates[0].link_id
              : result.link_id) ||
            null;
          this.blockedMessage = "";
          this.$modal.show("booking-intake-recover-modal");
        })
        .catch((err) => {
          const message =
            typeof err === "string" ? err : (err && err.error) || "Failed to diagnose booking.";
          this.openBlockedModal(row, { title: "Diagnose failed", message });
        })
        .finally(() => {
          this.diagnosingId = null;
        });
    },
    onActionChange() {
      if (this.selectedAction !== "attach_to_link") {
        return;
      }
      if (
        !this.selectedTargetLinkId &&
        this.diagnosis &&
        this.diagnosis.company_candidates &&
        this.diagnosis.company_candidates.length
      ) {
        this.selectedTargetLinkId = this.diagnosis.company_candidates[0].link_id;
      }
    },
    confirmRecover() {
      if (!this.selectedRow || !this.selectedAction) {
        return;
      }
      this.recoveringId = this.selectedRow.id;
      const targetLinkId =
        this.selectedAction === "attach_to_link" ? this.selectedTargetLinkId : null;
      targetsService
        .recoverBookingIntake(this.selectedRow.id, this.selectedAction, targetLinkId)
        .then((result) => {
          if (result && (result.status === "blocked" || result.error)) {
            this.openBlockedModal(this.selectedRow, {
              title: "Recover blocked",
              message: result.message || result.error || "Could not recover this booking.",
            });
            return;
          }
          this.closeRecoverModal();
          this.loadItems();
        })
        .catch((err) => {
          const message =
            typeof err === "string"
              ? err
              : (err && (err.message || err.error)) || "Recover failed.";
          this.openBlockedModal(this.selectedRow, {
            title: "Recover failed",
            message,
          });
        })
        .finally(() => {
          this.recoveringId = null;
        });
    },
    openBlockedModal(row, info) {
      this.selectedRow = row;
      this.modalMode = "blocked";
      this.diagnosis = null;
      this.selectedAction = null;
      this.selectedTargetLinkId = null;
      this.blockedTitle = (info && info.title) || "Cannot recover";
      this.blockedMessage = (info && info.message) || "Could not recover this booking.";
      this.$modal.show("booking-intake-recover-modal");
    },
    closeRecoverModal() {
      this.$modal.hide("booking-intake-recover-modal");
      this.selectedRow = null;
      this.modalMode = "confirm";
      this.diagnosis = null;
      this.selectedAction = null;
      this.selectedTargetLinkId = null;
      this.blockedTitle = "Cannot recover";
      this.blockedMessage = "";
    },
    actionLabel(action) {
      const map = {
        create_in_oliver: "Create in Oliver",
        attach_to_link: "Attach to opportunity",
        mark_processed: "Mark processed",
        ignore: "Ignore",
      };
      return map[action] || action;
    },
    caseBadgeClass(caseName) {
      if (caseName === "create_in_oliver") return "badge-danger";
      if (caseName === "attach_to_link" || caseName === "same_company_link") return "badge-warning";
      if (caseName === "already_in_oliver") return "badge-success";
      return "badge-secondary";
    },
    formatDateTime(value) {
      if (!value) return "—";
      return String(value).replace("T", " ").slice(0, 19);
    },
    statusBadgeClass(status) {
      if (status === "needs_recovery" || status === "failed") return "badge-danger";
      if (status === "external_created" || status === "received") return "badge-warning";
      if (status === "processed") return "badge-success";
      if (status === "processing") return "badge-info";
      if (status === "ignored") return "badge-secondary";
      return "badge-secondary";
    },
    rowRowClass(row) {
      if (row.status === "needs_recovery" || row.status === "failed") {
        return "booking-intake-row-alert";
      }
      const kind = this.linkMatchKind(row);
      if (kind === "created_new" || kind === "user_fuzzy" || kind === "recovery_create") {
        return "booking-intake-row-warn";
      }
      return "";
    },
    linkMatchKind(row) {
      const msg = (row && row.error_message) || "";
      const match = String(msg).match(/link_match:([a-z0-9_]+)/);
      return match ? match[1] : null;
    },
    linkMatchBadgeClass(kind) {
      if (kind === "created_new" || kind === "recovery_create") return "badge-danger";
      if (kind === "user_fuzzy") return "badge-warning text-dark";
      if (
        kind === "campaign_email_exact" ||
        kind === "user_email_exact" ||
        kind === "client_email_exact" ||
        kind === "client_domain" ||
        kind === "client_domain_contact" ||
        kind === "same_company_domain" ||
        kind === "same_company_domain_contact"
      ) {
        return "badge-info";
      }
      if (kind === "recovery_attach" || kind === "business_contact_hash") return "badge-secondary";
      return "badge-light border";
    },
    linkMatchLabel(kind) {
      const map = {
        created_new: "New link created",
        campaign_email_exact: "Matched: campaign + email",
        user_email_exact: "Matched: email",
        user_fuzzy: "Matched: fuzzy (domain/company)",
        client_email_exact: "Matched: client email",
        client_domain: "Matched: company domain",
        client_domain_contact: "Matched: domain via contact",
        same_company_domain: "Auto-attach: company domain",
        same_company_domain_contact: "Auto-attach: domain via contact",
        recovery_attach: "Recovery: attached",
        recovery_create: "Recovery: new link",
        business_contact_hash: "Matched: booking hash",
      };
      return map[kind] || kind;
    },
    linkMatchErrorClass(row) {
      const kind = this.linkMatchKind(row);
      if (kind === "created_new" || kind === "recovery_create" || kind === "user_fuzzy") {
        return "text-warning";
      }
      if (row.status === "failed" || row.status === "needs_recovery") {
        return "text-danger";
      }
      return "text-muted";
    },
    displayErrorMessage(row) {
      const msg = (row && row.error_message) || "";
      if (!msg) return "—";
      return String(msg).replace(/link_match:[a-z0-9_]+\s*/i, "").trim() || this.linkMatchLabel(this.linkMatchKind(row));
    },
  },
};
</script>

<style scoped>
.booking-intake-tab tr.booking-intake-row-alert td {
  background: #fff5f5;
}
.booking-intake-tab tr.booking-intake-row-alert:hover td {
  background: #ffe8e8;
}
.booking-intake-tab tr.booking-intake-row-warn td {
  background: #fffbf0;
}
.booking-intake-tab tr.booking-intake-row-warn:hover td {
  background: #fff3cd;
}
</style>
